/**
 * quality-monitor.js — OnlyFindz
 * Client-side quality monitoring without AI/external APIs.
 *
 * Features:
 *  1. Image quality checker (missing alt, tiny dimensions, wrong aspect ratio)
 *  2. Missing product description detector (Shopify Theme Editor only)
 *  3. Low stock warning badges on collection grids
 *  4. SEO meta-tag checker (theme editor only)
 *  5. Core Web Vitals logging (for internal monitoring)
 *
 * Runs only in Shopify theme editor (design mode) for admin views.
 * Production consumers: logged out — zero overhead.
 */

(function() {
  'use strict';

  var qualityMonitorState = window.__OnlyFindzQualityMonitorState || (window.__OnlyFindzQualityMonitorState = {
    bootstrapped: false,
    loadListenerAttached: false,
    cwvLogged: false
  });

  /* ── 1. Low Stock Badges on Collection Grid ── */
  /* Shows "Nur noch X übrig" on product cards when stock is low */

  function injectLowStockBadges() {
    var cards = document.querySelectorAll('[data-product-handle]');
    cards.forEach(function(card) {
      var stockEl = card.querySelector('[data-stock-count]');
      if (!stockEl) return;
      var stock = parseInt(stockEl.dataset.stockCount, 10);
      if (isNaN(stock) || stock <= 0 || stock > 5) return;
      if (card.querySelector('.qm-low-stock-badge')) return;

      var badge = document.createElement('div');
      badge.className = 'qm-low-stock-badge';
      badge.setAttribute('aria-label', 'Nur noch ' + stock + ' auf Lager');
      badge.innerHTML =
        '<span class="qm-low-stock-badge__dot" aria-hidden="true"></span>' +
        (stock === 1 ? 'Letzte Einheit' : 'Nur noch ' + stock + '');

      var mediaEl = card.querySelector('.card__media, .marketplace-product-card__media');
      if (mediaEl) {
        mediaEl.style.position = 'relative';
        mediaEl.appendChild(badge);
      }
    });
  }

  /* ── 2. Image Quality Checker (Design Mode only) ── */

  function checkImageQuality() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    var issues = [];
    var images = document.querySelectorAll('.product-media img, .card__media img, .marketplace-product-card__image');

    images.forEach(function(img) {
      if (!img.naturalWidth) return; // Not loaded yet

      // Missing alt text
      if (!img.alt || img.alt.trim() === '') {
        issues.push({ type: 'missing-alt', src: img.src, element: img });
      }

      // Very small image (thumbnail quality on PDP)
      if (img.naturalWidth < 300 && img.naturalHeight < 300) {
        issues.push({
          type: 'low-quality',
          src: img.src,
          width: img.naturalWidth,
          height: img.naturalHeight,
          element: img
        });
      }
    });

    if (issues.length > 0) {
      console.group('%c📸 OnlyFindz Quality Monitor: Bildprobleme', 'color: #f59e0b; font-weight: bold;');
      issues.forEach(function(issue) {
        if (issue.type === 'missing-alt') {
          console.warn('Fehlendes Alt-Attribut:', issue.src, issue.element);
        } else if (issue.type === 'low-quality') {
          console.warn('Zu kleines Bild (' + issue.width + 'x' + issue.height + 'px):', issue.src, issue.element);
        }
      });
      console.groupEnd();
    }

    return issues;
  }

  /* ── 3. SEO Meta-Tag Checker (Design Mode only) ── */

  function checkSeoMeta() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    var issues = [];

    var title = document.title;
    if (!title || title.length < 10) {
      issues.push('Seitentitel zu kurz oder fehlt: "' + title + '"');
    } else if (title.length > 70) {
      issues.push('Seitentitel zu lang (' + title.length + ' Zeichen, max. 70): "' + title.substring(0, 50) + '..."');
    }

    var metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc || !metaDesc.getAttribute('content') || metaDesc.getAttribute('content').length < 50) {
      issues.push('Meta-Beschreibung fehlt oder zu kurz');
    } else if (metaDesc.getAttribute('content').length > 160) {
      issues.push('Meta-Beschreibung zu lang (' + metaDesc.getAttribute('content').length + ' Zeichen, max. 160)');
    }

    var h1s = document.querySelectorAll('h1');
    if (h1s.length === 0) {
      issues.push('Kein H1-Element auf der Seite');
    } else if (h1s.length > 1) {
      issues.push('Mehrere H1-Elemente auf der Seite (' + h1s.length + ') — nur 1 empfohlen');
    }

    var ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage || !ogImage.getAttribute('content')) {
      issues.push('OG-Bild (Social Share) fehlt');
    }

    if (issues.length > 0) {
      console.group('%c🔍 OnlyFindz Quality Monitor: SEO-Probleme', 'color: #8b5cf6; font-weight: bold;');
      issues.forEach(function(issue) { console.warn(issue); });
      console.groupEnd();
    }
  }

  /* ── 4. Missing Description Checker (Design Mode only) ── */

  function checkMissingDescriptions() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    var descEl = document.querySelector('.product-tabs__description-content, .product__description');
    if (!descEl) return;

    var text = descEl.textContent.trim();
    if (text.length < 50) {
      console.warn(
        '%c⚠️ OnlyFindz Quality Monitor: Produktbeschreibung sehr kurz oder fehlt (' + text.length + ' Zeichen)',
        'color: #ef4444; font-weight: bold;'
      );
    }
  }

  /* ── 5. Core Web Vitals Logging ── */

  function logCoreWebVitals() {
    if (!window.PerformanceObserver) return;

    // LCP
    try {
      new PerformanceObserver(function(list) {
        var entries = list.getEntries();
        var last = entries[entries.length - 1];
        if (last && last.startTime > 2500) {
          console.warn(
            '%c⚡ LCP zu langsam: ' + Math.round(last.startTime) + 'ms (Ziel: <2500ms)',
            'color: #f59e0b; font-weight: bold;'
          );
        }
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    } catch(e) {}

    // CLS
    try {
      var clsValue = 0;
      new PerformanceObserver(function(list) {
        list.getEntries().forEach(function(entry) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        if (clsValue > 0.1) {
          console.warn(
            '%c⚡ CLS zu hoch: ' + clsValue.toFixed(4) + ' (Ziel: <0.1)',
            'color: #f59e0b; font-weight: bold;'
          );
        }
      }).observe({ type: 'layout-shift', buffered: true });
    } catch(e) {}
  }

  /* ── 6. Low Stock Badge Styles ── */

  function injectQualityMonitorStyles() {
    if (document.getElementById('quality-monitor-styles')) return;

    var style = document.createElement('style');
    style.id = 'quality-monitor-styles';
    style.textContent = [
      '.qm-low-stock-badge {',
      '  position: absolute;',
      '  bottom: 0.8rem;',
      '  left: 0.8rem;',
      '  z-index: 4;',
      '  display: inline-flex;',
      '  align-items: center;',
      '  gap: 0.4rem;',
      '  padding: 0.3rem 0.7rem;',
      '  border-radius: 999px;',
      '  background: rgba(239, 68, 68, 0.9);',
      '  color: #fff;',
      '  font-size: 1.1rem;',
      '  font-weight: 700;',
      '  letter-spacing: 0.01em;',
      '  pointer-events: none;',
      '  backdrop-filter: blur(4px);',
      '}',
      '.qm-low-stock-badge__dot {',
      '  width: 0.55rem;',
      '  height: 0.55rem;',
      '  border-radius: 50%;',
      '  background: #fff;',
      '  flex-shrink: 0;',
      '  animation: qm-pulse 1.4s ease-in-out infinite;',
      '}',
      '@keyframes qm-pulse {',
      '  0%, 100% { opacity: 1; transform: scale(1); }',
      '  50% { opacity: 0.6; transform: scale(0.85); }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  /* ── 7. Bootstrap ── */

  function bootstrap() {
    injectQualityMonitorStyles();
    injectLowStockBadges();

    if (qualityMonitorState.bootstrapped) return;
    qualityMonitorState.bootstrapped = true;

    // Design mode checks (admin preview only)
    if (window.Shopify && window.Shopify.designMode && !qualityMonitorState.loadListenerAttached) {
      qualityMonitorState.loadListenerAttached = true;
      window.addEventListener('load', function() {
        checkImageQuality();
        checkSeoMeta();
        checkMissingDescriptions();
      });
    }

    // Always log CWV in dev/preview
    if (!qualityMonitorState.cwvLogged && (location.hostname.includes('myshopify.com') || window.Shopify && window.Shopify.designMode)) {
      qualityMonitorState.cwvLogged = true;
      logCoreWebVitals();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Re-run after Shopify section loads (theme editor)
  document.addEventListener('shopify:section:load', function() {
    injectLowStockBadges();
  });

})();
